import { gql } from '@apollo/client';

// Test query to check backend connection
export const HEALTH_CHECK = gql`
  query HealthCheck {
    healthCheck
  }
`;

// Fragments
export const SERVICE_FRAGMENT = gql`
  fragment ServiceFragment on ServiceType {
    id
    name
    duration
    price
    isActive
    dateCreated
    dateUpdated
    isDeleted
  }
`;

export const STAFF_FRAGMENT = gql`
  fragment StaffFragment on StaffType {
    id
    firstName
    lastName
    username
    email
    fullName
    rating
    dateCreated
    dateUpdated
    isDeleted
    slug
    bio
    title
    avatar
    instagramUrl
    facebookUrl
    whatsappUrl
    telegramUrl
    linkedinUrl
    tiktokUrl
    portfolio {
      id
      url
    }
    services {
      ...ServiceFragment
    }
  }
  ${SERVICE_FRAGMENT}
`;

export const STORE_FRAGMENT = gql`
  fragment StoreFragment on StoreType {
    id
    name
    description
    address
    phone
    email
    weekdayStartTime
    weekdayEndTime
    weekendStartTime
    weekendEndTime
    rating
    dateCreated
    dateUpdated
    isDeleted
    isActive
    paymentRequired
    chargesEnabled
    platformSubscriptionId
    platformSubscriptionStatus
    platformBillingInterval
    platformSubscriptionCancelAtPeriodEnd
    seatCount
    activeSeatsUsed
    seatLicenseSubscriptionId
    instagramUrl
    facebookUrl
    whatsappUrl
    telegramUrl
    linkedinUrl
    avatar
    images {
      id
      image
      imageType
    }
  }
`;

export const CLIENT_FRAGMENT = gql`
  fragment ClientFragment on ClientType {
    id
    name
    phone
    email
    needToReach
    dateCreated
    dateUpdated
    isDeleted
  }
`;

export const SCHEDULE_FRAGMENT = gql`
  fragment ScheduleFragment on ScheduleType {
    id
    date
    store {
      ...StoreFragment
    }
    dateCreated
    dateUpdated
    isDeleted
  }
  ${STORE_FRAGMENT}
`;

export const STAFF_SCHEDULE_FRAGMENT = gql`
  fragment StaffScheduleFragment on StaffScheduleType {
    id
    staff {
      ...StaffFragment
    }
    schedule {
      ...ScheduleFragment
    }
    startTime
    endTime
    startBreak
    endBreak
    breakDuration
    dateCreated
    dateUpdated
    isDeleted
  }
  ${STAFF_FRAGMENT}
  ${SCHEDULE_FRAGMENT}
`;

export const APPOINTMENT_FRAGMENT = gql`
  fragment AppointmentFragment on AppointmentType {
    id
    scheduledDate
    status
    clientComment
    bookingGroupId
    paymentStatus
    paymentIntentId
    dateCreated
    dateUpdated
    isDeleted
    staff {
      ...StaffFragment
    }
    service {
      ...ServiceFragment
    }
    client {
      ...ClientFragment
    }
  }
  ${STAFF_FRAGMENT}
  ${SERVICE_FRAGMENT}
  ${CLIENT_FRAGMENT}
`;

export const NETWORK_FRAGMENT = gql`
  fragment NetworkFragment on NetworkType {
    id
    name
    dateCreated
    dateUpdated
    isDeleted
  }
`;

export const RATING_FRAGMENT = gql`
  fragment RatingFragment on RatingType {
    id
    rating
    comment
    clientName
    clientPhone
    clientEmail
    dateCreated
    dateUpdated
    isDeleted
  }
`;

export const STAFF_STORE_FRAGMENT = gql`
  fragment StaffStoreFragment on StaffStoreType {
    id
    role
    title
    isActive
    staff {
      id
      firstName
      lastName
      fullName
      email
      rating
      avatar
    }
    store {
      id
      name
    }
  }
`;

// Queries
export const GET_ALL_STAFF = gql`
  query GetAllStaff($storeId: ID!) {
    allStaff(storeId: $storeId) {
      ...StaffFragment
      staffStores {
        ...StaffStoreFragment
      }
    }
  }
  ${STAFF_FRAGMENT}
  ${SERVICE_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_STAFF_DETAILS = gql`
  query GetStaffDetails($staffId: ID!) {
    staffDetails(staffId: $staffId) {
      ...StaffFragment
      staffStores {
        ...StaffStoreFragment
      }
    }
  }
  ${STAFF_FRAGMENT}
  ${SERVICE_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_CURRENT_STAFF = gql`
  query GetCurrentStaff {
    myStaffDetails {
      ...StaffFragment
      staffStores {
        ...StaffStoreFragment
      }
    }
  }
  ${STAFF_FRAGMENT}
  ${SERVICE_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_AVAILABLE_STORES = gql`
  query GetAvailableStores {
    availableStores {
      ...StoreFragment
      network {
        ...NetworkFragment
      }
      staff {
        ...StaffStoreFragment
      }
    }
  }
  ${STORE_FRAGMENT}
  ${NETWORK_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_STORE_BY_ID = gql`
  query GetStoreById($storeId: ID!) {
    storeDetails(storeId: $storeId) {
      ...StoreFragment
      network {
        ...NetworkFragment
      }
      staff {
        ...StaffStoreFragment
      }
    }
  }
  ${STORE_FRAGMENT}
  ${NETWORK_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_ALL_STORES = gql`
  query GetAllStores {
    allStores {
      ...StoreFragment
      network {
        ...NetworkFragment
      }
      staff {
        ...StaffStoreFragment
      }
    }
  }
  ${STORE_FRAGMENT}
  ${NETWORK_FRAGMENT}
  ${STAFF_STORE_FRAGMENT}
`;

export const GET_ALL_SERVICES = gql`
  query GetAllServices($storeId: ID!) {
    allServices(storeId: $storeId) {
      ...ServiceFragment
      store {
        ...StoreFragment
      }
      staffs {
        id
        fullName
      }
    }
  }
  ${SERVICE_FRAGMENT}
  ${STORE_FRAGMENT}
`;

export const GET_ACTIVE_SERVICES = gql`
  query GetActiveServices($storeId: ID!) {
    activeServices(storeId: $storeId) {
      ...ServiceFragment
      store {
        ...StoreFragment
      }
      staffs {
        id
        fullName
      }
    }
  }
  ${SERVICE_FRAGMENT}
  ${STORE_FRAGMENT}
`;

export const GET_ALL_CLIENTS = gql`
  query GetAllClients {
    allClients {
      ...ClientFragment
    }
  }
  ${CLIENT_FRAGMENT}
`;

export const GET_ALL_APPOINTMENTS = gql`
  query GetAllAppointments(
    $storeId: ID!
    $startDate: Date
    $endDate: Date
    $staffId: ID
    $serviceId: ID
  ) {
    allAppointments(
      storeId: $storeId
      startDate: $startDate
      endDate: $endDate
      staffId: $staffId
      serviceId: $serviceId
    ) {
      ...AppointmentFragment
      store {
        ...StoreFragment
      }
    }
  }
  ${APPOINTMENT_FRAGMENT}
  ${STORE_FRAGMENT}
`;

// Paginated/filtered passed-appointments query for the admin panel.
// Uses the same allAppointments query as GET_ALL_APPOINTMENTS but passes
// the new statusIn / limit / offset / orderBy args added in the backend.
export const GET_PASSED_APPOINTMENTS = gql`
  query GetPassedAppointments(
    $storeId: ID!
    $startDate: Date
    $endDate: Date
    $staffId: ID
    $statusIn: [String]
    $limit: Int
    $offset: Int
    $orderBy: String
  ) {
    allAppointments(
      storeId: $storeId
      startDate: $startDate
      endDate: $endDate
      staffId: $staffId
      statusIn: $statusIn
      limit: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      ...AppointmentFragment
      store {
        ...StoreFragment
      }
    }
  }
  ${APPOINTMENT_FRAGMENT}
  ${STORE_FRAGMENT}
`;

// Total-count query — same filters as GET_PASSED_APPOINTMENTS minus
// pagination, used to compute the number of pagination pages.
export const GET_APPOINTMENTS_COUNT = gql`
  query GetAppointmentsCount(
    $storeId: ID!
    $startDate: Date
    $endDate: Date
    $staffId: ID
    $statusIn: [String]
  ) {
    appointmentsCount(
      storeId: $storeId
      startDate: $startDate
      endDate: $endDate
      staffId: $staffId
      statusIn: $statusIn
    )
  }
`;

export const GET_ALL_NETWORKS = gql`
  query GetAllNetworks {
    allNetworks {
      ...NetworkFragment
    }
  }
  ${NETWORK_FRAGMENT}
`;

export const GET_ALL_RATINGS = gql`
  query GetAllRatings($storeId: ID, $staffId: ID, $networkId: ID) {
    allRatings(storeId: $storeId, staffId: $staffId, networkId: $networkId) {
      ...RatingFragment
      staff {
        ...StaffFragment
      }
      store {
        ...StoreFragment
      }
      network {
        ...NetworkFragment
      }
      client {
        ...ClientFragment
      }
    }
  }
  ${RATING_FRAGMENT}
  ${STAFF_FRAGMENT}
  ${STORE_FRAGMENT}
  ${NETWORK_FRAGMENT}
  ${CLIENT_FRAGMENT}
`;

export const GET_AVAILABLE_APPOINTMENTS = gql`
  query GetAvailableAppointments(
    $storeId: ID!
    $date: Date!
    $services: [ID]
    $staffId: ID
  ) {
    availableAppointments(
      storeId: $storeId
      date: $date
      services: $services
      staffId: $staffId
    )
  }
`;

export const GET_APPOINTMENT_DETAILS = gql`
  query GetAppointmentDetails($appointmentId: ID!) {
    appointmentDetails(appointmentId: $appointmentId) {
      ...AppointmentFragment
      store {
        ...StoreFragment
      }
    }
  }
  ${APPOINTMENT_FRAGMENT}
  ${STORE_FRAGMENT}
`;

// Schedule-related queries
export const GET_SCHEDULE_LIST = gql`
  query GetScheduleList($storeId: ID!, $startDate: Date!, $duration: Int) {
    scheduleList(storeId: $storeId, startDate: $startDate, duration: $duration) {
      ...ScheduleFragment
      staffs {
        ...StaffFragment
      }
    }
  }
  ${SCHEDULE_FRAGMENT}
  ${STAFF_FRAGMENT}
`;

export const GET_SCHEDULE_DETAILS = gql`
  query GetScheduleDetails($scheduleId: ID!) {
    scheduleDetails(scheduleId: $scheduleId) {
      ...ScheduleFragment
      staffs {
        ...StaffFragment
      }
    }
  }
  ${SCHEDULE_FRAGMENT}
  ${STAFF_FRAGMENT}
`;

export const GET_STAFF_SCHEDULES = gql`
  query GetStaffSchedules($storeId: ID!) {
    allStaff(storeId: $storeId) {
      ...StaffFragment
      schedules {
        ...StaffScheduleFragment
      }
    }
  }
  ${STAFF_FRAGMENT}
  ${STAFF_SCHEDULE_FRAGMENT}
`;

export const GET_STAFF_SCHEDULE_DETAILS = gql`
  query GetStaffScheduleDetails($staffId: ID!) {
    staffDetails(staffId: $staffId) {
      ...StaffFragment
      schedules {
        ...StaffScheduleFragment
      }
    }
  }
  ${STAFF_FRAGMENT}
  ${STAFF_SCHEDULE_FRAGMENT}
`;

export const GET_STRIPE_ACCOUNT_STATUS = gql`
  query GetStripeAccountStatus($storeId: ID!) {
    stripeAccountStatus(storeId: $storeId) {
      accountId
      payoutsEnabled
      chargesEnabled
      requirementsDue
      detailsSubmitted
    }
  }
`;

export const GET_WEEKLY_SCHEDULE = gql`
  query GetWeeklySchedule($storeId: ID!, $startDate: Date!) {
    scheduleList(storeId: $storeId, startDate: $startDate, duration: 7) {
      ...ScheduleFragment
      staffs {
        ...StaffFragment
        schedules {
          ...StaffScheduleFragment
        }
      }
    }
  }
  ${SCHEDULE_FRAGMENT}
  ${STAFF_FRAGMENT}
  ${STAFF_SCHEDULE_FRAGMENT}
`;
